import { createContext, useCallback, useContext, useState, useEffect, ReactNode } from "react";

type Lang = "en" | "ar";

interface DashboardLangContextType {
  lang: Lang;
  toggleLang: () => void;
  isAr: boolean;
  t: (en: string, ar: string) => string;
}

const DashboardLangContext = createContext<DashboardLangContextType>({
  lang: "en",
  toggleLang: () => {},
  isAr: false,
  t: (en) => en,
});

export function DashboardLangProvider({ children }: { children: ReactNode }) {
  const [lang, setLang] = useState<Lang>(() => {
    return (localStorage.getItem("diyafa_lang") as Lang) ?? "en";
  });

  const toggleLang = () =>
    setLang((prev) => {
      const next = prev === "en" ? "ar" : "en";
      localStorage.setItem("diyafa_lang", next);
      return next;
    });

  const isAr = lang === "ar";

  useEffect(() => {
    document.documentElement.dir = isAr ? "rtl" : "ltr";
    document.documentElement.lang = isAr ? "ar" : "en";
  }, [isAr]);

  // Stable per language — pages list `t` in useCallback deps for their
  // loaders, so a fresh function every render would re-fetch in a loop.
  const t = useCallback((en: string, ar: string) => (isAr ? ar : en), [isAr]);

  return (
    <DashboardLangContext.Provider value={{ lang, toggleLang, isAr, t }}>
      {children}
    </DashboardLangContext.Provider>
  );
}

export const useDashLang = () => useContext(DashboardLangContext);
